import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    address: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6ljrrs6fojmejgaj3f0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          address: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get Your Free Quote
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-base"
                />
              </div>
              
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-base"
                />
              </div>
              
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-base"
                />
              </div>
              
              <div>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-base cursor-pointer"
                >
                  <option value="">Select a Service</option>
                  <option value="Pool Services">Pool Services</option>
                  <option value="Gutter Cleaning">Gutter Cleaning</option>
                  <option value="Pressure Washing">Pressure Washing</option>
                  <option value="Junk Removal">Junk Removal</option>
                  <option value="Soft Washing">Soft Washing</option>
                  <option value="Window Cleaning">Window Cleaning</option>
                  <option value="Fence Staining">Fence Staining</option>
                  <option value="Concrete Sealing">Concrete Sealing</option>
                  <option value="Outdoor Lighting">Outdoor Lighting</option>
                  <option value="Solar Panel Cleaning">Solar Panel Cleaning</option>
                  <option value="Dryer Vent Cleaning">Dryer Vent Cleaning</option>
                  <option value="Trash Can Cleaning">Trash Can Cleaning</option>
                </select>
              </div>
              
              <div>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Property Address"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-base"
                />
              </div>
              
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Additional Details"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all resize-none text-base"
                ></textarea>
                <div className="text-sm text-gray-500 mt-1 text-right">
                  {formData.message.length}/500 characters
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                <span>{isSubmitting ? 'Sending...' : 'Request Quote'}</span>
                <i className="ri-arrow-right-line"></i>
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                  Thank you! We'll respond within 2 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  Something went wrong. Please try again or call us directly.
                </div>
              )}
              
              <p className="text-sm text-gray-600 text-center">
                <i className="ri-time-line"></i> Response within 2 hours guaranteed
              </p>
            </form>
          </div>

          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl mb-8">
              <img 
                src="https://public.readdy.ai/ai/img_res/6ea19405f3bc4cfd677fd7d87064e3ec.jpg"
                alt="Contact Us"
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-xl p-6 shadow-lg flex items-center space-x-4 hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-phone-fill text-orange-500 text-xl"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Call or Text</div>
                  <a href="tel:+16262447341" className="text-lg font-bold text-gray-900 hover:text-orange-500 transition-colors cursor-pointer">
                    (626) 244-7341
                  </a>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg flex items-center space-x-4 hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-mail-fill text-orange-500 text-xl"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Email Us</div>
                  <a href="mailto:info@toolboxpropertypros.com" className="text-lg font-bold text-gray-900 hover:text-orange-500 transition-colors cursor-pointer">
                    info@toolboxpropertypros.com
                  </a>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg flex items-center space-x-4 hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-time-fill text-orange-500 text-xl"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Business Hours</div>
                  <div className="text-lg font-bold text-gray-900">
                    Mon-Sat: 7AM - 7PM
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}